import { z } from 'zod';

import { nonEmptyPatch } from './_patch.js';
import {
  RussiaRequestPrimaryBidSummarySchema,
  RussiaRequestPrimaryCostSummarySchema,
} from './russia-request-bids.js';
import { REQUEST_ITEM_PHOTOS_MAX } from './requests/items.js';
import { REFUSAL_COMMENT_MAX_LENGTH, RequestStatus } from './requests/status.js';
import { StorageObjectKeySchema } from './storage.js';

/**
 * @aiPurpose sentinel `endAddress` value the `from-parent` snapshot path
 *   writes when the caller omits a delivery address (#705). A row carrying
 *   this placeholder has NO real «Адрес доставки» yet, so the SEND gate
 *   (`russiaSendMissing`) treats it as missing for both kinds (#1579).
 * @aiInvariant single source of truth — the backend service and the send
 *   gate both import this constant; never duplicate the literal.
 */
export const RUSSIA_REQUEST_END_ADDRESS_PLACEHOLDER = '—';

/**
 * @aiPurpose wire enum classifying a `RussiaRequest`. Append-only — the
 *   Prisma `RussiaRequestType` enum mirrors this list under enum-sync.sh.
 */
export enum RussiaRequestType {
  LEAD = 'LEAD',
  WAREHOUSE = 'WAREHOUSE',
  IN_TRANSIT = 'IN_TRANSIT',
  URGENT = 'URGENT',
}

/**
 * @aiPurpose complexity discriminator for a `RussiaRequest`. `SIMPLE` is
 *   the default; `COMPLEX` is set at create time. Prisma `RussiaRequestKind`
 *   enum mirrors this list under enum-sync.sh.
 */
export enum RussiaRequestKind {
  SIMPLE = 'SIMPLE',
  COMPLEX = 'COMPLEX',
}

/**
 * @aiPurpose per-row audit-ledger action vocabulary for the Russia
 *   capsule. Slice 1 ships CREATE/UPDATE/DELETE; Slice 2b appended the
 *   four core transition codes (SEND/RETURN/PROCESS/CLOSE); Slice 2c
 *   appends ARCHIVE/UNARCHIVE. #1066 appends the three chat codes
 *   (CHAT_POSTED / CHAT_MARKED_UNREAD / CHAT_UNMARKED). #1544 brings the
 *   Russia lifecycle to China parity and appends TRANSITION_AUTO_START
 *   (implicit READY_FOR_WORK -> IN_WORK on first carrier add),
 *   TRANSITION_REOPEN, TRANSITION_SUCCESS, TRANSITION_REFUSE, and
 *   WINNER_PICK. Append-only — historical rows reference these literals
 *   forever.
 */
export enum RussiaRequestAuditAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  TRANSITION_SEND = 'TRANSITION_SEND',
  TRANSITION_AUTO_START = 'TRANSITION_AUTO_START',
  TRANSITION_RETURN = 'TRANSITION_RETURN',
  TRANSITION_PROCESS = 'TRANSITION_PROCESS',
  TRANSITION_REOPEN = 'TRANSITION_REOPEN',
  TRANSITION_CLOSE = 'TRANSITION_CLOSE',
  TRANSITION_ARCHIVE = 'TRANSITION_ARCHIVE',
  TRANSITION_UNARCHIVE = 'TRANSITION_UNARCHIVE',
  TRANSITION_SUCCESS = 'TRANSITION_SUCCESS',
  TRANSITION_REFUSE = 'TRANSITION_REFUSE',
  // WHY (#1837): explicit `PROCESSED -> POSTPONED` parking by the manager.
  //   Mirror of the China-side `RequestAuditAction.TRANSITION_POSTPONE`.
  //   Distinct from the finalisation codes so the per-request feed renders
  //   "request parked" rather than a success/refuse outcome; POSTPONED is
  //   active/non-terminal and clears no fields. The matching Prisma enum
  //   value + the additive expand migration land in the same PR;
  //   scripts/check-enum-sync.sh enforces the shared↔Prisma parity.
  TRANSITION_POSTPONE = 'TRANSITION_POSTPONE',
  WINNER_PICK = 'WINNER_PICK',
  CHAT_POSTED = 'CHAT_POSTED',
  CHAT_MARKED_UNREAD = 'CHAT_MARKED_UNREAD',
  CHAT_UNMARKED = 'CHAT_UNMARKED',
}

/**
 * @aiPurpose structured refusal reason stored on
 *   `RussiaRequest.refusalReason` when the customer declines a processed
 *   Russia quote (#1544). 1:1 mirror of the China `RequestRefusalReason`
 *   so the two capsules share the same analytics vocabulary. Append-only
 *   machine codes — the wire layer rejects strings outside this set. The
 *   Prisma `RussiaRequestRefusalReason` enum mirrors this list under
 *   enum-sync.sh.
 */
export enum RussiaRequestRefusalReason {
  QUOTE_RESPONSE_TOO_SLOW = 'QUOTE_RESPONSE_TOO_SLOW',
  PRICE_NOT_ACCEPTED = 'PRICE_NOT_ACCEPTED',
  CLIENT_UNRESPONSIVE = 'CLIENT_UNRESPONSIVE',
  OTHER = 'OTHER',
}

export const RussiaRequestRefusalReasonSchema = z.nativeEnum(RussiaRequestRefusalReason);

const RU_ID_REGEX = /^RU-\d{5}$/;

/**
 * @aiPurpose typed cross-link fallback projection for a `RussiaRequest`
 *   shown to an actor who cannot see the full row (#1077). Mirrors the
 *   shape of `LeadFallbackSchema` / `RequestFallbackSchema` so a
 *   client-side cross-link card can switch on `entityKind` without
 *   touching PII.
 * @aiSecurity per the cross-link allowlist, fallback exposes ONLY
 *   `entityKind`, `publicCode`, `status`, and the non-PII discriminator
 *   `type`. Never includes `name`, `endAddress`, manager identity,
 *   audit, chat, start-cities, items, boxes, or any other PII /
 *   business detail.
 * @aiInvariant `.strict()` rejects any extra key — the cross-link
 *   payload MUST fail loudly if a server-side mistake tries to pipe a
 *   richer surface through this projection.
 */
export const RussiaRequestFallbackSchema = z
  .object({
    entityKind: z.literal('russia_request'),
    publicCode: z.string().regex(RU_ID_REGEX),
    status: z.nativeEnum(RequestStatus),
    type: z.nativeEnum(RussiaRequestType).optional(),
  })
  .strict();
export type RussiaRequestFallback = z.infer<typeof RussiaRequestFallbackSchema>;

/**
 * @aiPurpose wire projection of a `RussiaRequestStartCity` join row.
 *   `arrivalCityId` is a UUID pointing to the shared `ArrivalCity`
 *   reference table; `sortOrder` carries the stable client-supplied
 *   order.
 */
export const RussiaRequestStartCitySchema = z
  .object({
    arrivalCityId: z.string().uuid(),
    sortOrder: z.number().int().nonnegative(),
  })
  .strict();
export type RussiaRequestStartCity = z.infer<typeof RussiaRequestStartCitySchema>;

/**
 * @aiPurpose wire projection of a `RussiaRequestItem` snapshot row.
 *   Standalone — no FK back to the originating `RequestItem`.
 */
export const RussiaRequestItemSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    // WHY (#1545): China-SIMPLE Positions parity. `brand` / `description`
    //   mirror the China `RequestItem` columns; both backfill to "" so
    //   pre-#1545 snapshot rows serialize cleanly.
    brand: z.string().max(200),
    description: z.string().max(2000),
    quantity: z.number().int().positive(),
    // WHY (#1545): product unit count, distinct from the logistics
    //   `quantity` mirror. Nullable — legacy snapshot rows carry NULL.
    productQuantity: z.number().int().positive().nullable(),
    material: z.string().optional(),
    weightKg: z.number().nonnegative().nullable(),
    volumeM3: z.number().nonnegative().nullable(),
    // INVARIANT (#1545): up to REQUEST_ITEM_PHOTOS_MAX photos per position.
    //   Each entry is a non-empty StorageObjectKey so a broken '' reference
    //   cannot persist; same `request-item-photo/` scope as China.
    photos: z.array(StorageObjectKeySchema).max(REQUEST_ITEM_PHOTOS_MAX),
    sortOrder: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type RussiaRequestItem = z.infer<typeof RussiaRequestItemSchema>;

/**
 * @aiPurpose POST /russia-requests/:id/items body — create one Russia
 *   «Простая» position. Mirror of the China `CreateRequestItemInput`
 *   minus the China-only dimensions/packages machinery: Russia SIMPLE
 *   positions are flat. `description` is OPTIONAL here — only the SEND
 *   gate (`russiaSimpleSendMissing`) requires it, so a half-typed DRAFT
 *   position can be saved without it. `name` is required.
 * @aiInvariant photos capped at REQUEST_ITEM_PHOTOS_MAX in every state;
 *   weight/volume are nonnegative-nullable so a draft can omit them.
 */
export const CreateRussiaRequestItemInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    brand: z.string().max(200).optional(),
    material: z.string().max(200).optional(),
    description: z.string().max(2000).optional(),
    weightKg: z.number().nonnegative().nullable().default(null),
    volumeM3: z.number().nonnegative().nullable().default(null),
    quantity: z.number().int().positive().default(1),
    productQuantity: z.number().int().positive().nullable().optional(),
    photos: z.array(StorageObjectKeySchema).max(REQUEST_ITEM_PHOTOS_MAX).default([]),
  })
  .strict();
export type CreateRussiaRequestItemInput = z.infer<typeof CreateRussiaRequestItemInputSchema>;

/**
 * @aiPurpose PATCH /russia-requests/:id/items/:itemId body — partial
 *   update of one position. Non-empty partial of the create shape; the
 *   service rejects a no-op patch. `description` stays optional here for
 *   the same draft-tolerance reason as create.
 */
export const PatchRussiaRequestItemInputSchema = nonEmptyPatch(
  z
    .object({
      name: z.string().min(1).max(200),
      brand: z.string().max(200),
      material: z.string().max(200),
      description: z.string().max(2000),
      weightKg: z.number().nonnegative().nullable(),
      volumeM3: z.number().nonnegative().nullable(),
      quantity: z.number().int().positive(),
      productQuantity: z.number().int().positive().nullable(),
      photos: z.array(StorageObjectKeySchema).max(REQUEST_ITEM_PHOTOS_MAX),
    })
    .strict()
    .partial(),
);
export type PatchRussiaRequestItemInput = z.infer<typeof PatchRussiaRequestItemInputSchema>;

/**
 * @aiPurpose wire projection of a `RussiaRequestBox` snapshot row.
 */
export const RussiaRequestBoxSchema = z
  .object({
    id: z.string().uuid(),
    sortOrder: z.number().int().nonnegative(),
    count: z.number().int().positive(),
    weightKg: z.number().nonnegative(),
    volumeM3: z.number().nonnegative(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type RussiaRequestBox = z.infer<typeof RussiaRequestBoxSchema>;

/**
 * @aiPurpose wire projection of a `RussiaRequest` row. Slice 1 ships
 *   the read shape WITHOUT carrier/bid arrays — they remain server-side
 *   until a later slice adds the sub-resource endpoints. `startCities`
 *   / `items` / `boxes` are emitted as empty arrays for now too, so the
 *   wire shape is stable for the UI to render before the sub-resource
 *   slices land. `primaryBidSummary` (added in #868) is the only
 *   additive bid-derived field on the list row.
 * @aiSecurity `endAddress` is freeform PII; visibility filter enforced
 *   on every list/detail read by the controller's
 *   `RussiaRequestsVisibility` guard. Realtime payloads (planned in a
 *   later slice) carry only the id + reason — never `endAddress` or
 *   actor identity.
 */
export const RussiaRequestSchema = z
  .object({
    id: z.string().regex(RU_ID_REGEX),
    parentRequestId: z.string().nullable(),
    type: z.nativeEnum(RussiaRequestType),
    // WHY (#1543): tolerant read for rolling-deploy / rollback skew —
    //   a new web bundle may briefly call a pre-#1543 backend that does
    //   not emit `kind`. `.optional().default(SIMPLE)` keeps the OUTPUT
    //   type non-null while parsing legacy `kind`-less responses as
    //   SIMPLE (semantically correct: pre-#1543 rows had no COMPLEX
    //   concept). Updated servers always emit `kind` (toWire is
    //   exhaustive). Mirrors the `dealId` forward-compat slot below.
    kind: z.nativeEnum(RussiaRequestKind).optional().default(RussiaRequestKind.SIMPLE),
    status: z.nativeEnum(RequestStatus),
    name: z.string().min(1).max(200),
    endAddress: z.string().min(1).max(2000),
    // WHY (#706): `manager` carries the owning user's display name so the
    // list table can render the manager column without a second
    // round-trip. Mirrors the China-side `Request.manager` flat-name
    // pattern. `managerId` below stays as the stable identity used for
    // client-side party gating and server-side filter parameters.
    manager: z.string().min(1),
    managerId: z.string().uuid(),
    logistId: z.string().uuid().nullable(),
    winnerBidId: z.string().uuid().nullable(),
    inWorkAt: z.string().datetime().nullable(),
    closedAt: z.string().datetime().nullable(),
    // WHY (#1544): reopen-cycle metadata mirroring the China-side
    //   `Request.reopenedAt` / `reopenedCount`. `reopenedAt` is stamped on
    //   the PROCESSED -> IN_WORK reopen; `reopenedCount` counts how many
    //   times the row was reopened. `.optional().default(...)` keeps the
    //   OUTPUT type non-null while parsing pre-#1544 responses that do not
    //   yet emit these slots (rolling-deploy / fixture forward-compat).
    reopenedAt: z.string().datetime().nullable().optional().default(null),
    reopenedCount: z.number().int().nonnegative().optional().default(0),
    // WHY (#1544): refusal outcome metadata mirroring the China-side
    //   `Request.refusalReason` / `refusalComment`. Non-null only on
    //   REFUSED rows. `comment` is freeform PII bounded by the wire layer.
    refusalReason: RussiaRequestRefusalReasonSchema.nullable().optional().default(null),
    refusalComment: z.string().max(REFUSAL_COMMENT_MAX_LENGTH).nullable().optional().default(null),
    // INVARIANT (#705 Slice 2c): mirrors the Prisma
    //   `RussiaRequest.archivedFromStatus` column. Non-null only while
    //   the row is in ARCHIVE — unarchive clears it. Legacy rows
    //   (pre-Slice-2c archives, if any are ever backfilled) may carry
    //   null and fall back to the matrix-default `to: CLOSED`.
    archivedFromStatus: z.nativeEnum(RequestStatus).nullable(),
    // WHY (#1546): Russia «Сложная» (COMPLEX) request-level totals. A
    //   COMPLEX request carries a single total weight (kg) / volume (m3)
    //   instead of per-position values. NULL for SIMPLE rows and COMPLEX
    //   drafts not yet filled. Mirror of the China-side `Request.weight` /
    //   `Request.volume` wire shape (`z.number().nonnegative().nullable()`).
    //   `.optional().default(null)` keeps the OUTPUT type non-null while
    //   parsing pre-#1546 responses / fixtures that don't yet emit these
    //   slots (rolling-deploy forward-compat); the real backend always
    //   emits them once #1546 ships.
    totalWeight: z.number().nonnegative().nullable().optional().default(null),
    totalVolume: z.number().nonnegative().nullable().optional().default(null),
    // WHY (#1077): mirror of the China-side `Request.leadId` link to
    //   `Lead.id`. `@db.Uuid` + ON DELETE SET NULL on the Prisma side —
    //   deleting a Lead nulls this slot but preserves the request.
    //   Always nullable: requests created before the row was bound to a
    //   Lead (and rows whose Lead was deleted) carry null here.
    leadId: z.string().uuid().nullable(),
    // WHY (#1088): commercial Deal link added in #1087. `@db.Uuid` +
    //   ON DELETE SET NULL — Deal deletion nulls the slot. Always
    //   nullable so legacy rows round-trip cleanly. `.optional()` +
    //   `.default(null)` so pre-#1088 MSW handlers and test fixtures
    //   that don't yet emit this slot still parse cleanly; the real
    //   backend always sets it (toWire is exhaustive).
    dealId: z.string().uuid().nullable().optional().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    startCities: z.array(RussiaRequestStartCitySchema),
    items: z.array(RussiaRequestItemSchema),
    boxes: z.array(RussiaRequestBoxSchema),
    primaryBidSummary: RussiaRequestPrimaryBidSummarySchema.nullable(),
    // WHY (#1548): identity-free logistics-cost channel. Always present on
    //   the wire (nullable, never PII-gated) so a Manager can see the cost
    //   without the PII-gated `primaryBidSummary`.
    primaryCostSummary: RussiaRequestPrimaryCostSummarySchema.nullable(),
  })
  .strict();
export type RussiaRequest = z.infer<typeof RussiaRequestSchema>;

/**
 * @aiPurpose POST /russia-requests/manual body — manual create path. The
 *   `managerId` is derived from the JWT at the controller, NEVER read
 *   from the body (mirror of `CreateRequestBody` invariant).
 *   `from-parent` snapshot creation uses
 *   `CreateRussiaRequestFromParentInputSchema` below.
 */
export const CreateRussiaRequestManualInputSchema = z
  .object({
    type: z.nativeEnum(RussiaRequestType),
    kind: z.nativeEnum(RussiaRequestKind).default(RussiaRequestKind.SIMPLE),
    name: z.string().min(1).max(200).trim(),
    endAddress: z.string().min(1).max(2000).trim(),
    startCityIds: z.array(z.string().uuid()).max(50).default([]),
    // WHY (#1546): Russia «Сложная» (COMPLEX) request-level totals. A
    //   COMPLEX request carries a single total weight (kg) / volume (m³)
    //   instead of per-position values. Nullable + default null so SIMPLE
    //   callers that omit the keys keep working — the service persists them
    //   verbatim. Mirror of the China-side `weight`/`volume` create inputs.
    totalWeight: z.number().nonnegative().nullable().default(null),
    totalVolume: z.number().nonnegative().nullable().default(null),
  })
  .strict();
export type CreateRussiaRequestManualInput = z.infer<typeof CreateRussiaRequestManualInputSchema>;

/**
 * @aiPurpose POST /russia-requests/from-parent/:requestId body — snapshot
 *   create from a China `Request` parent. Every field is optional;
 *   defaults are derived server-side (`name=parent.name`,
 *   `endAddress='—'` placeholder for the manager to fill in via PATCH).
 *   `type` is INTENTIONALLY NOT on the body — the issue spec
 *   (`type === 'LEAD'` enforced server-side) hard-codes it to `LEAD`
 *   here. Manual create remains the path for non-LEAD types.
 * @aiInvariant idempotent on `parentRequestId` (the column is `@unique`).
 *   A repeat call with the same parent returns the existing RussiaRequest
 *   unchanged regardless of body — `name`/`endAddress` from the second
 *   call are ignored. This is the contract sub-issue #707's button
 *   relies on. The unique-constraint loser in a concurrent double-POST
 *   race is caught at the service layer (Prisma P2002) and converted to
 *   the same idempotent reuse so neither caller sees a 500.
 */
export const CreateRussiaRequestFromParentInputSchema = z
  .object({
    name: z.string().min(1).max(200).trim().optional(),
    endAddress: z.string().min(1).max(2000).trim().optional(),
  })
  .strict();
export type CreateRussiaRequestFromParentInput = z.infer<
  typeof CreateRussiaRequestFromParentInputSchema
>;

/**
 * @aiPurpose PATCH /russia-requests/:id body. All fields optional;
 *   service rejects no-op patches.
 */
export const PatchRussiaRequestInputSchema = z
  .object({
    type: z.nativeEnum(RussiaRequestType).optional(),
    name: z.string().min(1).max(200).trim().optional(),
    endAddress: z.string().min(1).max(2000).trim().optional(),
    logistId: z.string().uuid().nullable().optional(),
    startCityIds: z.array(z.string().uuid()).max(50).optional(),
    // WHY (#1121 slice E): mirror of the China-side `leadId` PATCH slot.
    //   Accepts a UUID to bind the RussiaRequest to a CRM Lead, or `null`
    //   to clear the binding. Visibility on the target Lead is gated in
    //   the service layer (`assertLeadVisibleForLink`); link/unlink
    //   activity rows are emitted under both the old and new lead so
    //   either side's audit feed reflects the transfer.
    leadId: z.string().uuid().nullable().optional(),
    // WHY (#1088): symmetric `dealId` PATCH slot. Bind to a Deal id or
    //   pass `null` to clear. Visibility gated server-side via
    //   `assertDealVisibleForLink`; link/unlink activity rows emit under
    //   both the old and new deal.
    dealId: z.string().uuid().nullable().optional(),
    // WHY (#1546): Russia «Сложная» (COMPLEX) request-level totals PATCH
    //   slots. Pass a non-negative number to set, or `null` to clear.
    //   `.optional()` distinguishes "not provided" (untouched) from
    //   `null` (cleared). Mirror of the China-side writable totals.
    totalWeight: z.number().nonnegative().nullable().optional(),
    totalVolume: z.number().nonnegative().nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'at least one field is required',
  });
export type PatchRussiaRequestInput = z.infer<typeof PatchRussiaRequestInputSchema>;

/**
 * @aiPurpose GET /russia-requests query — keyset-cursor list. Mirrors
 *   `RequestListQuerySchema` (china) at the wire level so the
 *   russia-requests list can share the same q+cursor UI primitives.
 *   Surface stays minimal: status/type filter, optional manager scope,
 *   `q` substring match, cursor pagination. SLA filters land with the
 *   broader UI slice (epic #935).
 */
export const RussiaRequestListQuerySchema = z
  .object({
    status: z.nativeEnum(RequestStatus).optional(),
    type: z.nativeEnum(RussiaRequestType).optional(),
    // WHY (#706): manager filter is functionally meaningful only for users
    //   holding `russiaRequests.scope.all` (admin / logist_ru). For a
    //   manager without scope.all the visibility predicate already
    //   intersects rows to `managerId = self.sub`; passing a different
    //   managerId here just yields an empty page. The wire stays a single
    //   UUID for the initial slice; multi-select / array transport can
    //   land later mirroring the China-side `SingleManagerIdOrArraySchema`
    //   if/when the UI gains a multi-pick widget.
    managerId: z.string().uuid().optional(),
    // INVARIANT (#938): substring search; `.trim()` runs first so the
    //   parsed `q` is whitespace-normalised before `.min(1)` rejects an
    //   empty input — without it `q=%20%20%20` would slip past `.min(1)`
    //   and turn into a no-op filter at the SQL layer, and `q=%20alpha`
    //   would behave differently from `q=alpha`. `.max(200)` keeps the
    //   ILIKE workload bounded. No C0 control characters — the predicate
    //   is plain ILIKE on `name` plus `startsWith` on `id`, so chr(1)
    //   is not a separator hazard here as it is on china (which joins
    //   fields with chr(1) into a denormalised `searchHash`), but
    //   rejecting C0 still makes prod input shape explicit.
    q: z
      .string()
      .trim()
      .min(1)
      .max(200)
      // eslint-disable-next-line no-control-regex -- intentional: rejecting C0 makes prod input shape explicit.
      .regex(/^[^\u0000-\u001f]+$/, 'q must not contain control characters')
      .optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type RussiaRequestListQuery = z.infer<typeof RussiaRequestListQuerySchema>;

/**
 * @aiPurpose paginated GET /russia-requests response.
 */
export const RussiaRequestListResponseSchema = z
  .object({
    items: z.array(RussiaRequestSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type RussiaRequestListResponse = z.infer<typeof RussiaRequestListResponseSchema>;

/**
 * @aiPurpose minimal manager reference used by the Russia request-list
 *   manager picker. Returned by `GET /api/russia-requests/manager-options`,
 *   one entry per distinct `RussiaRequest.managerId` visible to the caller
 *   under the russia-request visibility predicate. Mirrors the China-side
 *   `RequestManagerOption` shape so the web picker swap stays a one-line
 *   change.
 * @aiInvariant `id` is the manager's user id (UUID) — tight `.uuid()` so
 *   options that round-trip into `RussiaRequestListQuerySchema.managerId`
 *   (also `.uuid()`) cannot fail validation on the follow-up
 *   `/russia-requests?managerId=...` call.
 */
export const RussiaRequestManagerOptionSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();
export type RussiaRequestManagerOption = z.infer<typeof RussiaRequestManagerOptionSchema>;

/**
 * @aiPurpose response wire shape for `GET /api/russia-requests/manager-options`.
 *   Flat array (no envelope) — matches the China-side
 *   `RequestManagerOptionsResponseSchema`.
 */
export const RussiaRequestManagerOptionsResponseSchema = z.array(RussiaRequestManagerOptionSchema);
export type RussiaRequestManagerOptionsResponse = z.infer<
  typeof RussiaRequestManagerOptionsResponseSchema
>;

// WHY (#1548): the `RussiaRequestAuditEntry*` wire schemas moved to the
//   sibling `russia-request-audit.ts` to keep this file under the 600-line
//   hard limit. They re-export from the same shared barrel, so external
//   imports are unchanged.
